import { ContentLoading, InlineLoading } from "../components/ContentLoading";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, KeyRound, Plus, Trash2 } from "lucide-react";
import { useState, type SyntheticEvent } from "react";

import type {
  OpsMcpKeyCreate,
  OpsMcpKeyCustomer,
  OpsMcpKeyExpirationDays,
  OpsMcpScope,
} from "../../shared/operations-schemas";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { createOpsMcpKey, listOpsMcpKeys, revokeOpsMcpKey } from "./api";

const SCOPE_LABELS: Record<OpsMcpScope, string> = {
  "ops:read": "Read customer and tool knowledge",
  "ops:search": "Search customer and tool knowledge",
  "ops:write": "Upload files and maintain context",
};
const EXPIRATION_OPTIONS: OpsMcpKeyExpirationDays[] = [7, 30, 90];

function date(timestamp: number | null) {
  return timestamp === null ? "Never" : new Date(timestamp * 1_000).toLocaleString();
}

function SecretResult({ token, onDismiss }: { token: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-4 rounded-sm border border-primary/30 bg-info-background p-4">
      <p className="font-semibold">Copy this key now</p>
      <p className="mt-1 text-sm text-muted-foreground">
        This is the only time the full key is shown. DAS stores only its SHA-256 hash.
      </p>
      <pre className="mt-3 overflow-x-auto rounded-sm border bg-card p-3 text-xs">
        <code>{token}</code>
      </pre>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          onClick={() => {
            void navigator.clipboard?.writeText(token).then(() => {
              setCopied(true);
            });
          }}
          type="button"
        >
          {copied ? <Check /> : <Copy />} {copied ? "Copied" : "Copy key"}
        </Button>
        <Button onClick={onDismiss} type="button" variant="outline">
          I saved it
        </Button>
      </div>
    </div>
  );
}

function preferredScopes(customer: OpsMcpKeyCustomer | undefined) {
  return (["ops:read", "ops:search"] as OpsMcpScope[]).filter((scope) =>
    customer?.allowedScopes.includes(scope),
  );
}

function CustomerField({
  customer,
  customers,
  onChange,
}: {
  customer: OpsMcpKeyCustomer;
  customers: OpsMcpKeyCustomer[];
  onChange: (organizationId: string) => void;
}) {
  return (
    <div>
      <Label htmlFor="mcp-key-customer">Customer</Label>
      <select
        className="mt-1 min-h-10 w-full rounded-sm border border-input bg-card px-3 text-sm"
        id="mcp-key-customer"
        onChange={(event) => {
          onChange(event.currentTarget.value);
        }}
        value={customer.id}
      >
        {customers.map((candidate) => (
          <option key={candidate.id} value={candidate.id}>
            {candidate.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function ScopeFields({
  allowedScopes,
  scopes,
  onChange,
}: {
  allowedScopes: OpsMcpScope[];
  scopes: OpsMcpScope[];
  onChange: (scope: OpsMcpScope, checked: boolean) => void;
}) {
  return (
    <fieldset>
      <legend className="text-sm font-medium">Capabilities</legend>
      <div className="mt-2 grid gap-2">
        {allowedScopes.map((scope) => (
          <label className="flex items-start gap-3 rounded-sm border p-3 text-sm" key={scope}>
            <input
              checked={scopes.includes(scope)}
              className="mt-0.5 size-4"
              onChange={(event) => {
                onChange(scope, event.currentTarget.checked);
              }}
              type="checkbox"
            />
            <span>
              <span className="block font-mono text-xs font-bold">{scope}</span>
              <span className="text-muted-foreground">{SCOPE_LABELS[scope]}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function ExpirationField({
  value,
  onChange,
}: {
  value: OpsMcpKeyExpirationDays;
  onChange: (value: OpsMcpKeyExpirationDays) => void;
}) {
  return (
    <div>
      <Label htmlFor="mcp-key-expiration">Expires after</Label>
      <select
        className="mt-1 min-h-10 w-full rounded-sm border border-input bg-card px-3 text-sm"
        id="mcp-key-expiration"
        onChange={(event) => {
          onChange(Number(event.currentTarget.value) as OpsMcpKeyExpirationDays);
        }}
        value={value}
      >
        {EXPIRATION_OPTIONS.map((days) => (
          <option key={days} value={days}>
            {days} days
          </option>
        ))}
      </select>
    </div>
  );
}

function KeyForm({
  customers,
  error,
  pending,
  onCreate,
  onResetResult,
}: {
  customers: OpsMcpKeyCustomer[];
  error: Error | null;
  pending: boolean;
  onCreate: (input: OpsMcpKeyCreate) => void;
  onResetResult: () => void;
}) {
  const [label, setLabel] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<OpsMcpScope[]>(["ops:read", "ops:search"]);
  const [expiresInDays, setExpiresInDays] = useState<OpsMcpKeyExpirationDays>(30);
  const customer = customers.find((candidate) => candidate.id === organizationId) ?? customers[0];
  const allowedScopes = customer?.allowedScopes ?? [];
  const scopes = selectedScopes.filter((scope) => allowedScopes.includes(scope));

  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!customer || scopes.length === 0) return;
    onCreate({ label, organizationId: customer.id, scopes, expiresInDays });
  }
  if (!customer) return null;
  return (
    <form className="mt-4 space-y-4" onSubmit={submit}>
      <div>
        <Label htmlFor="mcp-key-label">Key name</Label>
        <Input
          id="mcp-key-label"
          maxLength={80}
          onChange={(event) => {
            setLabel(event.currentTarget.value);
          }}
          placeholder="Joe's local Codex"
          required
          value={label}
        />
      </div>
      <CustomerField
        customer={customer}
        customers={customers}
        onChange={(nextId) => {
          setOrganizationId(nextId);
          setSelectedScopes(preferredScopes(customers.find(({ id }) => id === nextId)));
          onResetResult();
        }}
      />
      <ScopeFields
        allowedScopes={allowedScopes}
        onChange={(scope, checked) => {
          setSelectedScopes((current) =>
            checked
              ? [...new Set([...current, scope])]
              : current.filter((candidate) => candidate !== scope),
          );
        }}
        scopes={scopes}
      />
      <ExpirationField onChange={setExpiresInDays} value={expiresInDays} />
      {error ? <FieldError>{error.message}</FieldError> : null}
      <Button disabled={pending || scopes.length === 0} type="submit">
        <KeyRound /> {pending ? "Generating…" : "Generate key"}
      </Button>
    </form>
  );
}

function KeyCreationForm() {
  const queryClient = useQueryClient();
  const directory = useQuery({ queryKey: ["ops", "mcp-keys"], queryFn: listOpsMcpKeys });
  const mutation = useMutation({
    mutationFn: createOpsMcpKey,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["ops", "mcp-keys"] }),
  });
  const customers = directory.data?.customers ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="size-5" /> Generate a personal MCP key
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Use a key only for an AI client that needs a fixed Authorization header. It is bound to
          you, one customer, the capabilities below, and an expiration.
        </p>
        {directory.isPending ? (
          <InlineLoading label="Loading available customers…" className="mt-4" />
        ) : null}
        {directory.error ? <FieldError>{directory.error.message}</FieldError> : null}
        {directory.data && customers.length === 0 ? (
          <p className="mt-4 rounded-sm border border-dashed p-3 text-sm text-muted-foreground">
            You do not currently have customer knowledge access, so no key can be generated.
          </p>
        ) : null}
        <KeyForm
          customers={customers}
          error={mutation.error}
          onCreate={mutation.mutate}
          onResetResult={mutation.reset}
          pending={mutation.isPending}
        />
        {mutation.data ? (
          <SecretResult onDismiss={mutation.reset} token={mutation.data.token} />
        ) : null}
      </CardContent>
    </Card>
  );
}

function ExistingKeys() {
  const queryClient = useQueryClient();
  const directory = useQuery({ queryKey: ["ops", "mcp-keys"], queryFn: listOpsMcpKeys });
  const revoke = useMutation({
    mutationFn: revokeOpsMcpKey,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["ops", "mcp-keys"] }),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>My MCP keys</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {directory.isPending ? <ContentLoading label="Loading keys…" /> : null}
        {directory.error ? <FieldError>{directory.error.message}</FieldError> : null}
        {directory.data?.keys.length === 0 ? (
          <p className="rounded-sm border border-dashed p-3 text-sm text-muted-foreground">
            No personal MCP keys yet.
          </p>
        ) : null}
        {directory.data?.keys.map((key) => (
          <div className="rounded-sm border p-4" key={key.id}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-semibold">{key.label}</p>
                <p className="text-sm text-muted-foreground">{key.customerName}</p>
              </div>
              <Badge variant={key.status === "active" ? "outline" : "secondary"}>
                {key.status}
              </Badge>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {key.scopes.map((scope) => (
                <Badge key={scope} variant="secondary">
                  {scope}
                </Badge>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Expires {date(key.expiresAt)} · Last used {date(key.lastUsedAt)}
            </p>
            {key.status === "active" ? (
              <Button
                className="mt-3"
                disabled={revoke.isPending}
                onClick={() => {
                  revoke.mutate(key.id);
                }}
                size="sm"
                type="button"
                variant="outline"
              >
                <Trash2 /> Revoke
              </Button>
            ) : null}
          </div>
        ))}
        {revoke.error ? <FieldError>{revoke.error.message}</FieldError> : null}
      </CardContent>
    </Card>
  );
}

export function PersonalMcpKeys({ endpoint }: { endpoint: string }) {
  return (
    <section className="mt-5">
      <div>
        <h2 className="text-xl font-bold">Personal MCP keys</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure the same endpoint with an HTTP header: Authorization: Bearer &lt;personal
          key&gt;. OAuth remains the recommended connection for interactive clients.
        </p>
        <pre className="mt-3 overflow-x-auto rounded-sm border bg-muted/60 p-3 text-xs">
          <code>{`{ "url": "${endpoint}", "headers": { "Authorization": "Bearer <personal-key>" } }`}</code>
        </pre>
      </div>
      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <KeyCreationForm />
        <ExistingKeys />
      </div>
    </section>
  );
}
